export const UNKNOWN_FAILURE_REASON = "Unknown error";

/**
 * Keys carrying a human-readable reason, cleanest first. `error_message` is
 * ChargeBee's gateway detail, `message` covers `Error` and Stripe, and
 * `error_text` is the noisy summary ("…; code: …; request-id: …") kept last as
 * a fallback for attempts that carry no detail object.
 */
const MESSAGE_KEYS = [
    "error_message",
    "message",
    "displayMessage",
    "error_msg",
    "error_text",
] as const;

/**
 * Keys carrying a machine-readable code (`expired_card`, `card_declined`, …).
 * Worth more than the message for grouping in BigQuery, because the message is
 * often a generic "there was an error processing your request".
 */
const CODE_KEYS = [
    "error_code",
    "api_error_code",
    "decline_code",
    "code",
    "type",
] as const;

/**
 * Where the real error hides. A refused wallet payment rejects with the entire
 * payment intent, which says nothing about the failure at its top level: the
 * reason sits under `active_payment_attempt.error_detail`. Ordered most
 * specific first, and searched before the current level, so the clean
 * code + message of a detail object wins over the summary above it.
 */
const NESTED_KEYS = [
    "error_detail",
    "active_payment_attempt",
    "payment_attempts",
    "error",
    "cause",
    "data",
    "raw",
    "response",
] as const;

/**
 * The `NESTED_KEYS` that hold a *transport* payload rather than a gateway one:
 * an `error_message` under `response`, `data` or `raw` is the body our own API
 * wrote, and must neither be quoted to a cardholder nor read as the issuer
 * having ruled on the card.
 *
 * A strip-list, not an allow-list, because `error` and `cause` are SDK wrappers
 * that can hold anything — a refused intent nested inside one is still the
 * gateway's verdict, and treating them as non-gateway silently swallowed it.
 */
const TRANSPORT_KEYS = new Set<string>(["data", "raw", "response"]);

/**
 * The one key holding text ChargeBee/Stripe wrote for the cardholder ("Your
 * card has insufficient funds."). Read separately from `MESSAGE_KEYS` so the
 * broad fallbacks there — SDK `message`, the `error_text` summary — can never
 * be mistaken for it.
 */
const GATEWAY_MESSAGE_KEYS = ["error_message"] as const;

/** The issuer's granular reason, one level below a blanket `card_declined`. */
const DECLINE_CODE_KEYS = ["decline_code"] as const;

/** Deep enough for payment_intent → active_payment_attempt → error_detail. */
const MAX_DEPTH = 3;
const MIN_PROVIDER_HTTP_ERROR_STATUS = 400;
const MAX_PROVIDER_HTTP_ERROR_STATUS = 599;

export type FailureDetails = {
    code?: string;
    message?: string;
    /**
     * The gateway's own sentence for a refused payment, already written for the
     * cardholder: "Your card has insufficient funds.", "Your card was
     * declined." The only field here that may be shown to a customer, hence the
     * two rules enforced while reading it:
     *  - it comes from `error_message` and no other key
     *    (`GATEWAY_MESSAGE_KEYS`). `message` carries SDK internals and
     *    `error_text` appends "; code: …; request-id: …", so neither may stand
     *    in for it — and it is dropped when found inside a transport payload
     *    (`TRANSPORT_KEYS`), whose author is our own API, not the gateway.
     *  - `code` is never substituted for a missing message. `card_declined`
     *    means nothing to the person holding the card.
     *
     * `undefined` means the failure carries no cardholder-facing verdict (an
     * SDK, transport or mount error, or an intent that was never attempted),
     * and the caller must fall back to its own copy.
     */
    gatewayMessage?: string;
    /** `insufficient_funds`, `do_not_honor`, … — never shown, only reported. */
    declineCode?: string;
    providerHttpStatus?: number;
};

const readString = (
    source: Record<string, unknown>,
    keys: readonly string[]
): string | undefined => {
    for (const key of keys) {
        const value = source[key];
        if (typeof value === "string" && value.trim().length > 0) {
            return value.trim();
        }
    }

    return undefined;
};

const readProviderHttpStatus = (
    source: Record<string, unknown>
): number | undefined => {
    for (const key of ["status", "statusCode", "status_code"]) {
        const value = source[key];
        if (
            typeof value === "number" &&
            Number.isInteger(value) &&
            value >= MIN_PROVIDER_HTTP_ERROR_STATUS &&
            value <= MAX_PROVIDER_HTTP_ERROR_STATUS
        ) {
            return value;
        }
    }

    return undefined;
};

/** What a sweep of the nested containers turned up. */
type NestedRead = {
    /** The deepest level that actually named the failure, if one did. */
    details?: FailureDetails;
    /** The first provider status seen while sweeping, when none named it. */
    status?: number;
};

/**
 * Searches `NESTED_KEYS` for the level that names the failure. Nested levels are
 * read before the current one so the clean code + message of a gateway detail
 * wins over the noisy summary above it.
 */
const readNestedError = (
    source: Record<string, unknown>,
    depth: number
): NestedRead => {
    let status: number | undefined;

    for (const key of NESTED_KEYS) {
        const details = readUnknown(source[key], depth - 1);
        if (!details) continue;

        if (!details.message && !details.code) {
            status ??= details.providerHttpStatus;
            continue;
        }

        // Crossing into a transport payload keeps the reason but drops the
        // claim that an issuer said it.
        return TRANSPORT_KEYS.has(key)
            ? {
                details: {
                    ...details,
                    gatewayMessage: undefined,
                    declineCode: undefined,
                },
            }
            : { details };
    }

    return { status };
};

const readErrorObject = (
    source: Record<string, unknown>,
    depth: number
): FailureDetails | undefined => {
    const currentStatus = readProviderHttpStatus(source);
    const nested: NestedRead =
        depth > 0 ? readNestedError(source, depth) : {};

    if (nested.details) {
        return {
            ...nested.details,
            providerHttpStatus:
                nested.details.providerHttpStatus ?? currentStatus,
        };
    }

    const message = readString(source, MESSAGE_KEYS);
    const code = readString(source, CODE_KEYS);
    const providerHttpStatus = currentStatus ?? nested.status;

    return message || code || providerHttpStatus
        ? {
            code,
            message,
            gatewayMessage: readString(source, GATEWAY_MESSAGE_KEYS),
            declineCode: readString(source, DECLINE_CODE_KEYS),
            providerHttpStatus,
        }
        : undefined;
};

const readUnknown = (
    value: unknown,
    depth: number
): FailureDetails | undefined => {
    if (typeof value === "string") {
        const message = value.trim();
        return message ? { message } : undefined;
    }

    if (Array.isArray(value)) {
        // Entering an entry costs a level, as descending into a nested key
        // does, so a nested or self-referential array cannot recurse forever.
        if (depth <= 0) return undefined;

        // A status-only entry (an attempt carrying just an HTTP status) is
        // kept, but must not end the search: a later entry may hold the code
        // and message that explain the failure.
        let statusOnly: FailureDetails | undefined;

        for (const entry of value) {
            const reason = readUnknown(entry, depth - 1);
            if (!reason) continue;
            if (reason.message || reason.code) return reason;

            statusOnly ??= reason;
        }

        return statusOnly;
    }

    if (value && typeof value === "object") {
        return readErrorObject(value as Record<string, unknown>, depth);
    }

    return undefined;
};

/**
 * The reason a payment failed, as a string safe to send to analytics.
 *
 * Keeps what the regular checkout reports for a plain `Error` (its message),
 * and additionally understands what ChargeBee rejects wallet and card payments
 * with — the whole payment intent, which `String(err)` renders as
 * "[object Object]". That is the reason a shared resolver exists.
 *
 * An unrecognised shape falls back to a constant instead of being serialized.
 * That intent object also carries `payer_info` (the payer's email, phone and
 * address) and `checkout_details` (their IP and user agent), none of which may
 * leave the browser — which is why the keys above are an allowlist rather than
 * "whatever string this object holds".
 */
export function resolveFailureReason(
    error: unknown,
    fallback: string = UNKNOWN_FAILURE_REASON
): string {
    return formatFailureReason(resolveFailureDetails(error), fallback);
}

/**
 * The same reason string from details already resolved, so a caller that needs
 * several answers about one failure walks it once instead of asking each
 * resolver to walk it again.
 */
export function formatFailureReason(
    { code, message }: FailureDetails,
    fallback: string = UNKNOWN_FAILURE_REASON
): string {
    // Both are useful and neither implies the other: the code groups the
    // failure, the message explains it.
    if (message && code) return `${code}: ${message}`;

    return message ?? code ?? fallback;
}

/**
 * Reads only documented/observed error containers and fields. Values remain
 * untrusted and must be normalized before they are sent to telemetry.
 */
export function resolveFailureDetails(error: unknown): FailureDetails {
    return readUnknown(error, MAX_DEPTH) ?? {};
}
